import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { isFunction, isPlainObject } from 'lodash';
import Button from './Button';

function isSameModal(a, b) {
  return a === b || (a && b && a.props.modalKey === b.props.modalKey);
}

export function ModalStack({ name = 'default' }) {
  const [stack, setStack] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const dom = useRef();

  useEffect(() => {
    window.__modalStacks = window.__modalStacks || {};
    window.__modalStacks[name] = {
      push: (modal, options) => {
        const entry = {};

        entry.options = {
          closeOnBackgroundClick: true,
          ...options,
        };

        entry.promise = new Promise((resolve) => {
          entry.done = resolve;
        });

        entry.handle = {
          wait: () => entry.promise,
          pop: () => entry.handle.close(true),
          close: (popStack = false) => {
            setStack((stack) => {
              const index = stack.findIndex((e) => e.handle === entry.handle);
              if (index < 0) {
                // This modal is not on the stack any more
                return stack;
              }

              // Callback will be called after the entries are done being popped off the stack
              setCallbacks((callbacks) => [
                ...callbacks,
                ...(popStack ? stack.slice(index) : [stack[index]]).map(
                  (entry) => entry.done,
                ),
              ]);

              return popStack
                ? stack.slice(0, index)
                : [...stack.slice(0, index), ...stack.slice(index + 1)];
            });
          },
        };

        entry.modal = isFunction(modal) ? modal(entry.handle) : modal;

        setStack((stack) => [...stack, entry]);
        return entry.handle;
      },
      pop: (modal) => {
        setStack((stack) => {
          const entry = modal
            ? stack.find((entry) => isSameModal(entry.modal, modal))
            : stack[stack.length - 1];

          if (entry) {
            entry.handle.pop();
          }

          return stack.filter((item) => item !== entry);
        });
      },
      close: (modal) => {
        setStack((stack) => {
          const entry = stack.find((entry) => isSameModal(entry.modal, modal));
          entry && entry.handle.close();
          return stack.filter((item) => item !== entry);
        });
      },
      get dom() {
        return dom;
      },
    };

    return () => {
      delete window.__modalStacks[name];
    };
  }, [name]);

  useEffect(() => {
    if (callbacks.length === 0) {
      return;
    }

    for (let callback of callbacks) {
      callback();
    }
  }, [callbacks]);

  if (stack.length === 0) {
    return null;
  }

  return (
    <div ref={dom} className="modal-stack">
      {stack.map(({ handle, modal, options }, index) => {
        // Dim the background unless there is already an empty dimming layer immediately below this modal
        const dimmed = index === 0 || stack[index - 1].modal;

        return (
          <div
            key={(modal && modal.key) || index}
            className="modal-wrapper"
            style={{ zIndex: index * 3 }}
          >
            <div
              className={cn('modal-background', { dimmed })}
              style={{ zIndex: index * 3 + 1 }}
              onClick={(e) => {
                if (
                  options.closeOnBackgroundClick &&
                  e.target === e.currentTarget
                ) {
                  e.preventDefault();
                  handle.pop();
                }
              }}
            ></div>
            {modal && (
              <div
                className={options.className || 'modal-content'}
                style={{ zIndex: index * 3 + 2 }}
              >
                {modal}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ConfirmationWithInput({
  message,
  onOk,
  okText,
  onCancel,
  cancelText,
  maxInputLength,
  validateInput,
}) {
  const [inputText, setInputText] = useState('');

  return (
    <React.Fragment>
      <div className="modal-confirm-content">
        {message}
        {maxInputLength ? (
          <input
            id="modal-content"
            maxLength={maxInputLength}
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
            }}
          />
        ) : (
          <textarea
            id="modal-content"
            rows="5"
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
            }}
          />
        )}
      </div>
      <div className="modal-confirm-actions">
        <Button
          variant="outline"
          onClick={() => {
            onCancel?.();
          }}
        >
          {cancelText || 'Cancel'}
        </Button>
        <Button
          disabled={validateInput ? !validateInput(inputText) : false}
          onClick={async () => {
            onOk?.(inputText);
          }}
        >
          {okText || 'Ok'}
        </Button>
      </div>
    </React.Fragment>
  );
}

export class Modal {
  static isShowingModal() {
    for (let layer in window.__modalStacks || {}) {
      if (this.getStack(layer).dom.current) {
        return true;
      }
    }

    return false;
  }

  static getStack(name = 'default') {
    return window.__modalStacks && window.__modalStacks[name];
  }

  static push(modal, options) {
    const { layer } = options || {};
    const stack = this.getStack(layer || 'default');

    if (!stack) {
      return null;
    }

    // Blur the active element to prevent double clicking
    document.activeElement?.blur();

    return stack && stack.push(modal, options);
  }

  static pop(modal, layer = 'default') {
    const stack = this.getStack(layer);
    return stack && stack.pop(modal);
  }

  static close(modal, layer = 'default') {
    const stack = this.getStack(layer);
    return stack && stack.pop(modal);
  }

  static alert(params) {
    if (!isPlainObject(params) || React.isValidElement(params)) {
      params = { message: params };
    }

    const { message, onOk, okText, ...options } = params || {};

    const modal = (
      <React.Fragment>
        <div className="modal-alert-content">{message}</div>
        <div className="modal-alert-actions">
          <Button
            onClick={() => {
              handle.pop();
              onOk && onOk();
            }}
          >
            {okText || 'Ok'}
          </Button>
        </div>
      </React.Fragment>
    );

    const handle = this.push(modal, {
      closeOnBackgroundClick: false,
      ...options,
    });

    return handle.wait();
  }

  static confirm(params) {
    if (!isPlainObject(params) || React.isValidElement(params)) {
      params = { message: params };
    }

    const { message, onOk, okText, onCancel, cancelText, ...options } = params;

    let result = 'canceled';
    const modal = (
      <React.Fragment>
        <div className="modal-confirm-content">{message}</div>
        <div className="modal-confirm-actions">
          <Button
            variant="outline"
            onClick={() => {
              handle.pop();
              onCancel && onCancel();
            }}
          >
            {cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={async () => {
              result = 'confirmed';
              handle.pop();
              onOk && onOk();
            }}
          >
            {okText || 'Ok'}
          </Button>
        </div>
      </React.Fragment>
    );

    const handle = this.push(modal, {
      closeOnBackgroundClick: false,
      ...options,
    });

    return handle.wait().then(() => {
      return result;
    });
  }

  static confirmWithInput(params) {
    if (!isPlainObject(params) || React.isValidElement(params)) {
      params = { message: params };
    }

    const {
      message,
      onOk,
      okText,
      onCancel,
      cancelText,
      maxInputLength,
      validateInput,
      ...options
    } = params;

    let result = { input: '', result: 'canceled' };
    const modal = (
      <ConfirmationWithInput
        message={message}
        okText={okText}
        cancelText={cancelText}
        maxInputLength={maxInputLength}
        validateInput={validateInput}
        onOk={async (inputText) => {
          result.result = 'confirmed';
          result.input = inputText;
          handle.pop();
          onOk?.(inputText);
        }}
        onCancel={() => {
          handle.pop();
          onCancel && onCancel();
        }}
      />
    );

    const handle = this.push(modal, {
      closeOnBackgroundClick: false,
      ...options,
    });

    return handle.wait().then(() => {
      return result;
    });
  }

  static confirmWithDropdownInput(params) {
    if (!isPlainObject(params) || React.isValidElement(params)) {
      params = { message: params };
    }

    const {
      message,
      noOptionsMessage,
      onOk,
      okText,
      onCancel,
      cancelText,
      dropdownOptions,
      ...options
    } = params;

    let selection = dropdownOptions.length > 0 ? dropdownOptions[0].value : '';

    const handleChangeDropdown = (event) => {
      selection = event.target.value;
    };

    let result = { selection, result: 'canceled' };
    const modal = (
      <React.Fragment>
        <div className="modal-confirm-content">
          {dropdownOptions.length > 0 ? message : noOptionsMessage}
          {dropdownOptions.length > 0 ? (
            <select
              id="modal-confirm-content-dropdown"
              onChange={handleChangeDropdown}
            >
              {dropdownOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <div className="modal-confirm-actions">
          <Button
            variant="outline"
            onClick={() => {
              handle.pop();
              onCancel && onCancel();
            }}
          >
            {cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={async () => {
              result.result = 'confirmed';
              result.selection = selection;
              handle.pop();
              onOk && onOk(result);
            }}
          >
            {okText || 'Ok'}
          </Button>
        </div>
      </React.Fragment>
    );

    const handle = this.push(modal, {
      closeOnBackgroundClick: false,
      ...options,
    });

    return handle.wait().then(() => {
      return result;
    });
  }
}
